// The picker's state: the filter, the selection, the two-step act (#7802). `RepoEnablementPanel`
// renders it. Selecting "all matching" holds the FILTER rather than ids, and a filter change drops
// the selection, so a confirm can never carry rows the reader is no longer looking at.

import { MaintenanceRecipe, RepoBulkAction } from '@developerz/domain';
import { useQueryClient } from '@tanstack/solid-query';
import { batch, createMemo, createSignal } from 'solid-js';
import { t } from '../i18n';
import { createMutation } from './mutation';
import { OVERVIEW_COUNTS_QUERY_KEY } from './overview-counts';
import { usePagedQuery } from './paged';
import {
  dryRunRepoBulk,
  REPO_PICK_MAX_IDS,
  type RepoBulkAnswer,
  type RepoBulkRequest,
  type RepoEnablementFilter,
  type RepoEnablementFilterPatch,
  type RepoEnablementRow,
  type RepoSelection,
  runRepoBulk,
  useRepoEnablementFacets,
  useRepoEnablementPage,
} from './repo-enablement';
import { REPOS_QUERY_KEY } from './repos';

/** A confirm in hand: the request, and the server's own count of what it does. */
export type RepoBulkPending = { readonly request: RepoBulkRequest; readonly dry: RepoBulkAnswer };

export function createRepoEnablementPicker(input: {
  readonly token: string | null;
  readonly onChanged: (() => void) | undefined;
}) {
  const queryClient = useQueryClient();
  const [filter, setFilter] = createSignal<RepoEnablementFilter>({});
  const [picked, setPicked] = createSignal<ReadonlySet<string>>(new Set());
  const [allMatching, setAllMatching] = createSignal(false);
  const [recipe, setRecipe] = createSignal<MaintenanceRecipe>(MaintenanceRecipe.MaintainAutoMerge);
  const [pending, setPending] = createSignal<RepoBulkPending | null>(null);
  const [result, setResult] = createSignal<RepoBulkAnswer | null>(null);

  let page: ReturnType<typeof useRepoEnablementPage> | undefined;
  const list = usePagedQuery<RepoEnablementRow>((cursor) => {
    page = useRepoEnablementPage(filter, cursor, input.token);
    return page;
  });
  const facets = useRepoEnablementFacets(input.token);
  /** The server's count travels on the first page, so it holds as later pages load. */
  const matching = createMemo<number | null>((held) => page?.data?.matching ?? held, null);

  /**
   * What the person asked for moved: a dry run still out answers for something they replaced, and
   * a confirm on screen would act on it. Every input the act reads goes through here.
   */
  let inputsVersion = 0;
  const inputsChanged = (): void => {
    inputsVersion += 1;
    setPending(null);
  };
  const clearSelection = (): void => {
    inputsChanged();
    batch(() => {
      setPicked(new Set<string>());
      setAllMatching(false);
    });
  };
  const applyFilter = (patch: RepoEnablementFilterPatch): void => {
    batch(() => {
      const next: Record<string, unknown> = { ...filter(), ...patch };
      for (const key of Object.keys(next)) if (next[key] === undefined) delete next[key];
      setFilter(next as RepoEnablementFilter);
      clearSelection();
      setResult(null);
      list.reset();
    });
  };
  const togglePick = (id: string): void => {
    inputsChanged();
    const next = new Set(picked());
    if (!next.delete(id)) next.add(id);
    setPicked(next);
  };
  const pickPage = (): void => {
    inputsChanged();
    setPicked(new Set([...picked(), ...list.rows().map((row) => row.id)]));
  };

  const selection = (): RepoSelection =>
    allMatching() ? { kind: 'all_matching' } : { kind: 'ids', ids: [...picked()] };
  const selectedCount = (): number => (allMatching() ? (matching() ?? 0) : picked().size);
  const tooMany = (): boolean => !allMatching() && picked().size > REPO_PICK_MAX_IDS;

  const ask = createMutation({
    call: async (action: RepoBulkAction) => {
      const version = inputsVersion;
      const request: RepoBulkRequest = {
        action,
        filter: filter(),
        selection: selection(),
        ...(action === RepoBulkAction.Enable ? { recipe: recipe() } : {}),
      };
      return { version, request, dry: await dryRunRepoBulk(request, input.token) };
    },
    fallback: t('repo_enablement.dry_run_failed'),
    onSuccess: (asked) => {
      if (asked.version !== inputsVersion) return;
      // Nothing would change: there is no act to confirm, and the reasons are the answer.
      if (asked.dry.applied === 0) setResult(asked.dry);
      else setPending(asked);
    },
  });

  const reread = (): void => {
    clearSelection();
    list.reload();
    void queryClient.invalidateQueries({ queryKey: [REPOS_QUERY_KEY] });
    void queryClient.invalidateQueries({ queryKey: [OVERVIEW_COUNTS_QUERY_KEY] });
    input.onChanged?.();
  };
  const act = createMutation({
    call: async (asked: RepoBulkPending) => {
      try {
        await runRepoBulk(asked.request, asked.dry.matched ?? 0, input.token, setResult);
      } finally {
        // Partial or whole, what landed is audited and on the account: show the account's truth.
        reread();
      }
    },
    fallback: t('repo_enablement.act_failed'),
  });
  const confirm = (): void => {
    const asked = pending();
    if (asked === null) return;
    batch(() => {
      setPending(null);
      setResult(null);
    });
    void act.run(asked);
  };

  return {
    filter,
    applyFilter,
    facets,
    list,
    matching,
    picked,
    allMatching,
    selectAllMatching: () => {
      inputsChanged();
      setAllMatching(true);
    },
    togglePick,
    pickPage,
    clearSelection,
    selectedCount,
    tooMany,
    recipe,
    setRecipe: (next: MaintenanceRecipe) => {
      inputsChanged();
      setRecipe(next);
    },
    ask,
    act,
    pending,
    cancel: () => setPending(null),
    confirm,
    result,
  };
}

export type RepoEnablementPicker = ReturnType<typeof createRepoEnablementPicker>;
